import React, { useRef, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { OfflineIndicator } from "@/components/OfflineIndicator";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import { DirContext, depthOf } from "@/lib/nav";
import { ProtectedRoute, Splash } from "@/components/ProtectedRoute";
import { InstallPrompt } from "@/components/InstallPrompt";
import { UpdatePrompt } from "@/components/UpdatePrompt";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import ResetPassword from "@/pages/ResetPassword";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import JobDetail from "@/pages/JobDetail";
import ReportEditor from "@/pages/ReportEditor";
import PublicShare from "@/pages/PublicShare";
import Account from "@/pages/Account";
import ActivityFeed from "@/pages/ActivityFeed";
import Testers from "@/pages/Testers";
import VerticalLanding from "@/pages/VerticalLanding";
import PunchListApp from "@/pages/PunchListApp";
import Pricing from "@/pages/Pricing";
import About from "@/pages/About";
import Features from "@/pages/Features";
import Customers from "@/pages/Customers";
import Contact from "@/pages/Contact";
import Compare from "@/pages/Compare";
import Demo from "@/pages/Demo";
import NotFound from "@/pages/NotFound";
import { Terms, Privacy } from "@/pages/Legal";

function RootRoute() {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <Landing />;
}

// Auto "Add to Home Screen" prompt — only for signed-in members, so it never
// nags visitors on the landing or public share pages.
function InstallGate() {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  // Never stack the install popup on top of the signup / trial-start moment.
  if (loading || !user || pathname === "/onboarding") return null;
  // Don't auto-nag on entry (e.g. right after a visitor taps the demo). The
  // prompt is fired explicitly after the user creates their first job.
  return <InstallPrompt auto={false} />;
}

function AnimatedRoutes() {
  const location = useLocation();
  const prevPath = useRef(location.pathname);
  const prevDepth = useRef(depthOf(location.pathname));
  const d = depthOf(location.pathname);
  let dir = "none";
  if (location.pathname !== prevPath.current) {
    dir = d > prevDepth.current ? "forward" : d < prevDepth.current ? "back" : "none";
  }
  useEffect(() => {
    prevPath.current = location.pathname;
    prevDepth.current = d;
  });

  // Capture marketing attribution (UTM / referrer) once, on first tagged visit.
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const src = q.get("utm_source");
    if (src && !localStorage.getItem("sp_attr")) {
      localStorage.setItem("sp_attr", JSON.stringify({
        utm_source: src, utm_medium: q.get("utm_medium") || "",
        utm_campaign: q.get("utm_campaign") || "", referrer: document.referrer || "",
      }));
    }
  }, [location.search]);

  return (
    <DirContext.Provider value={dir}>
      <AnimatePresence initial={false} custom={dir} mode="sync">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<RootRoute />} />          <Route path="/punch-list-contractors" element={<VerticalLanding vertical="contractors" />} />
          <Route path="/punch-list-app" element={<PunchListApp />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/about" element={<About />} />
          <Route path="/features" element={<Features />} />
          <Route path="/customers" element={<Customers />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/vs/send-proof-vs-fieldwire" element={<Compare variant="fieldwire" />} />
          <Route path="/vs/send-proof-vs-group-texts" element={<Compare variant="groupTexts" />} />
          <Route path="/demo" element={<Demo />} />
          <Route path="/make-ready" element={<VerticalLanding vertical="makeReady" />} />
          <Route path="/realtor-repairs" element={<VerticalLanding vertical="realtors" />} />
          <Route path="/drywall-punch-list" element={<VerticalLanding vertical="drywall" />} />
          <Route path="/painting-punch-list" element={<VerticalLanding vertical="painting" />} />
          <Route path="/electrical-punch-list" element={<VerticalLanding vertical="electrical" />} />
          <Route path="/restoration-punch-list" element={<VerticalLanding vertical="restoration" />} />
          <Route path="/multifamily-turns" element={<VerticalLanding vertical="multifamily" />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/reset" element={<ResetPassword />} />
          <Route path="/r/:token" element={<PublicShare />} />
          <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/jobs/:id" element={<ProtectedRoute requireSub><JobDetail /></ProtectedRoute>} />
          <Route path="/jobs/:id/reports/:reportId" element={<ProtectedRoute requireSub><ReportEditor /></ProtectedRoute>} />
          <Route path="/activity" element={<ProtectedRoute requireSub><ActivityFeed /></ProtectedRoute>} />
          <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
          <Route path="/testers" element={<ProtectedRoute><Testers /></ProtectedRoute>} />
          <Route path="/marketing" element={<Navigate to="/testers" replace />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </AnimatePresence>
    </DirContext.Provider>
  );
}

const MKT_PATHS = new Set(["/", "/pricing", "/features", "/customers", "/about", "/contact", "/demo", "/punch-list-app", "/punch-list-contractors", "/make-ready", "/realtor-repairs", "/terms", "/privacy"]);

function PageviewTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!MKT_PATHS.has(pathname)) return; // only count marketing-site visits
    try {
      let vid = localStorage.getItem("sp_vid");
      if (!vid) {
        vid = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
        localStorage.setItem("sp_vid", vid);
      }
      const utm = new URLSearchParams(window.location.search).get("utm_source") || "";
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/pageview`, {
        method: "POST",
        keepalive: true,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path: pathname, referrer: document.referrer || "", utm_source: utm, visitor_id: vid }),
      }).catch(() => {});
    } catch (_) { /* never let tracking break the page */ }
  }, [pathname]);
  return null;
}

function Canonical() {
  const { pathname } = useLocation();
  useEffect(() => {
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    // Self-referencing: the page's own absolute URL, without query/hash so
    // ?utm_, trailing and www variants collapse to one canonical per page.
    const path = pathname === "/" ? "/" : pathname.replace(/\/$/, "");
    link.setAttribute("href", `${window.location.origin}${path}`);
  }, [pathname]);
  return null;
}
function App() {
  return (
    <ThemeProvider>
    <BrowserRouter>
      <AuthProvider>
        <Canonical />
        <PageviewTracker />
        <AnimatedRoutes />
        <InstallGate />
        <UpdatePrompt />
        <OfflineIndicator />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
